import React, { useEffect, useState } from "react";
import { Check, Star, Flame, Clock } from "lucide-react";

// Launch promo ends 30 days from first render — change this to a fixed date
// (e.g. new Date("2026-06-30T23:59:59Z")) once the actual end date is known.
const LAUNCH_END_KEY = "iariya_promo_end";
const getEndDate = () => {
  try {
    const stored = localStorage.getItem(LAUNCH_END_KEY);
    if (stored) {
      const d = new Date(stored);
      if (!isNaN(d.getTime())) return d;
    }
  } catch {
    /* noop */
  }
  const d = new Date();
  d.setDate(d.getDate() + 30);
  try {
    localStorage.setItem(LAUNCH_END_KEY, d.toISOString());
  } catch {
    /* noop */
  }
  return d;
};

const CountdownTile = ({ value, label, testid }) => (
  <div
    data-testid={testid}
    className="bg-white text-ink border-2 border-ink rounded-2xl px-4 py-3 min-w-[80px] sm:min-w-[96px] shadow-brutal text-center"
  >
    <p className="font-display text-3xl sm:text-4xl font-black leading-none tabular-nums">
      {String(value).padStart(2, "0")}
    </p>
    <p className="mt-1 text-[10px] font-black uppercase tracking-widest text-stone-600">
      {label}
    </p>
  </div>
);

const Countdown = () => {
  const [endDate] = useState(getEndDate);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const diff = Math.max(0, endDate.getTime() - now.getTime());
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  const ended = diff <= 0;

  return (
    <div
      data-testid="pricing-countdown"
      className="mt-10 flex flex-col items-center gap-4"
    >
      <p className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.25em] text-stone-700">
        <Clock size={14} />{" "}
        {ended ? "Launch window closed" : "Launch pricing ends in"}
      </p>
      {!ended && (
        <div className="flex items-center gap-2 sm:gap-3">
          <CountdownTile value={days} label="Days" testid="countdown-days" />
          <span className="font-display text-3xl font-black text-stone-700">
            :
          </span>
          <CountdownTile value={hours} label="Hours" testid="countdown-hours" />
          <span className="font-display text-3xl font-black text-stone-700">
            :
          </span>
          <CountdownTile
            value={minutes}
            label="Min"
            testid="countdown-minutes"
          />
          <span className="font-display text-3xl font-black text-stone-700 hidden sm:inline">
            :
          </span>
          <div className="hidden sm:block">
            <CountdownTile
              value={seconds}
              label="Sec"
              testid="countdown-seconds"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const tiers = [
  {
    name: "Basic",
    setup: { now: "£99", was: "£198" },
    monthly: { now: "£79", was: "£158" },
    blurb: "A clever AI assistant for your most painful task. Live in 7 days.",
    features: [
      "1 AI assistant (chat or WhatsApp)",
      "Calendar & email integration",
      "30 min monthly tweaks",
      "Email support",
    ],
    bg: "bg-white text-ink",
    cta: "bg-ink text-white",
    badge: null,
    testid: "pricing-basic",
  },
  {
    name: "Standard",
    setup: { now: "£199", was: "£398" },
    monthly: { now: "£179", was: "£358" },
    blurb: "The sweet spot. Voice + chat + automations that talk to each other.",
    features: [
      "Voice agent + chat assistant",
      "CRM / Sheets integration",
      "Lead-capture funnels",
      "2 hrs monthly optimisation",
      "Priority WhatsApp support",
    ],
    bg: "bg-[var(--pink)] text-white",
    cta: "bg-white text-ink",
    badge: "Most popular",
    testid: "pricing-standard",
  },
  {
    name: "Premium",
    setup: { now: "£299", was: "£598" },
    monthly: { now: "£279", was: "£558" },
    blurb: "Multi-channel ops. We embed with your team for bigger workflows.",
    features: [
      "Unlimited AI assistants",
      "Custom integrations (n8n, Zapier, API)",
      "Dedicated automation engineer",
      "Quarterly strategy reviews",
      "SLA-backed uptime",
    ],
    bg: "bg-[var(--blue)] text-white",
    cta: "bg-[var(--yellow)] text-ink",
    badge: null,
    testid: "pricing-premium",
  },
];

const PriceRow = ({ label, now, was, lightText }) => (
  <div className="flex items-baseline justify-between gap-3 py-1">
    <span
      className={`text-xs font-bold uppercase tracking-widest ${
        lightText ? "text-stone-600" : "text-white/75"
      }`}
    >
      {label}
    </span>
    <div className="flex items-baseline gap-2">
      <span
        className={`text-sm line-through ${
          lightText ? "text-stone-500" : "text-white/55"
        }`}
        data-testid={`${label.toLowerCase()}-was`}
      >
        {was}
      </span>
      <span
        className="font-display text-3xl sm:text-4xl font-black"
        data-testid={`${label.toLowerCase()}-now`}
      >
        {now}
      </span>
    </div>
  </div>
);

export const Pricing = () => {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="border-b-2 border-ink py-24 lg:py-32 bg-[var(--cream)] relative overflow-hidden"
    >
      {/* tape strip */}
      <div className="absolute top-0 left-0 right-0 h-3 tape" />

      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          {/* Launch promo badge */}
          <div
            data-testid="pricing-promo-badge"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--yellow)] border-2 border-ink rounded-full font-black uppercase text-xs tracking-widest shadow-brutal tilt-left"
          >
            <Flame size={14} className="text-[var(--pink)]" /> Half-price launch
            promo · Limited time
          </div>

          <p className="uppercase text-sm font-bold tracking-[0.25em] text-[var(--blue)] mt-6">
            Pricing
          </p>
          <h2 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight">
            Launch pricing. <br />
            <span className="text-[var(--pink)]">Honest.</span>{" "}
            <span className="text-[var(--blue)]">Halved.</span>
          </h2>
          <p className="mt-5 text-lg text-stone-700">
            For a limited time we're slashing every setup fee and monthly
            subscription by <strong>50%</strong>. Lock in the launch rate today
            and keep it for the lifetime of your plan.
          </p>

          {/* Countdown */}
          <Countdown />
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((t) => {
            const lightText = t.bg === "bg-white text-ink";
            return (
              <div
                key={t.name}
                data-testid={t.testid}
                className={`relative border-2 border-ink rounded-3xl p-7 shadow-brutal-hover shadow-brutal ${t.bg} flex flex-col`}
              >
                {/* Half-price corner ribbon */}
                <div
                  className="absolute -top-3 -right-3 bg-[var(--yellow)] text-ink border-2 border-ink rounded-2xl px-3 py-1 text-xs font-black uppercase tracking-wider shadow-brutal rotate-6"
                  data-testid={`${t.testid}-ribbon`}
                >
                  50% off
                </div>

                {t.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[var(--yellow)] text-ink border-2 border-ink rounded-full px-4 py-1 text-xs font-black uppercase tracking-wider flex items-center gap-1.5">
                    <Star size={12} className="fill-current" /> {t.badge}
                  </div>
                )}

                <h3 className="font-display text-3xl font-black">{t.name}</h3>
                <p
                  className={`mt-2 ${lightText ? "text-stone-700" : "text-white/90"}`}
                >
                  {t.blurb}
                </p>

                <div
                  className={`mt-6 border-2 border-ink rounded-2xl p-4 ${
                    lightText ? "bg-[var(--paper)]" : "bg-black/15"
                  }`}
                >
                  <PriceRow
                    label="Setup"
                    now={t.setup.now}
                    was={t.setup.was}
                    lightText={lightText}
                  />
                  <div
                    className={`my-2 h-[2px] ${
                      lightText ? "bg-stone-900/30" : "bg-white/30"
                    }`}
                  />
                  <PriceRow
                    label="Monthly"
                    now={t.monthly.now}
                    was={t.monthly.was}
                    lightText={lightText}
                  />
                </div>

                <ul className="mt-6 space-y-3 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check
                        size={18}
                        className="mt-0.5 shrink-0"
                        strokeWidth={3}
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="#book"
                  data-testid={`${t.testid}-cta`}
                  className={`btn-ink mt-8 inline-flex items-center justify-center px-5 py-3 ${t.cta}`}
                >
                  Claim launch price
                </a>
              </div>
            );
          })}
        </div>

        <p className="text-center mt-10 text-stone-700">
          Prices are crossed-out at the standard rate. Pay the launch price for
          as long as you stay subscribed.
        </p>
      </div>
    </section>
  );
};
